import { Box, Typography, useTheme, useMediaQuery } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AnimatedBlueButton } from "src/components/common";
import { useClipPathOnScroll } from "src/hooks/useClipPathOnScroll";

type AwArdItem = {
  title: string;
  text: string;
};

const ReasonsToChooseUs: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [activeIndex, setActiveIndex] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const { ref, clipPath } = useClipPathOnScroll({ direction: "left", initClipPath: "inset(0% 50% 50% 0% round 8px)" });
  const smallMd = useMediaQuery(theme.breakpoints.down("md"));

  const awardList: AwArdItem[] = [
    {
      title: t("home.chooseUs.awards.title1"),
      text: t("home.chooseUs.awards.text1"),
    },
    {
      title: t("home.chooseUs.awards.title2"),
      text: t("home.chooseUs.awards.text2"),
    },
    {
      title: t("home.chooseUs.awards.title3"),
      text: t("home.chooseUs.awards.text3"),
    },
    {
      title: t("home.chooseUs.awards.title4"),
      text: t("home.chooseUs.awards.text4"),
    },
  ];

  const startAutoSwitch = useCallback(() => {
    intervalRef.current = setInterval(() => {
      setActiveIndex(prev => (prev + 1) % awardList.length);
    }, 6000);
  }, [awardList.length]);

  useEffect(() => {
    startAutoSwitch();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [startAutoSwitch]);

  return (
    <Box
      sx={{
        bgcolor: "#f9f9fb",
        paddingBlock: "clamp(60px,10.417vw,200px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        color: "#323e59",
      }}
    >
      <Box
        className="container"
        sx={{
          maxWidth: "1920px",
          gridTemplateColumns: "repeat(12,1fr)",
          columnGap: "clamp(10px,.833vw,16px)",
          paddingInline: "clamp(16px,4.167vw,80px)",
          display: "grid",
          position: "relative",
          width: "100%",
          zIndex: 1,
          [theme.breakpoints.down("md")]: { display: "flex", flexDirection: "column" },
        }}
      >
        <Box
          className="header"
          sx={{
            display: "grid",
            gap: "clamp(16px,1.563vw,30px) clamp(10px,.833vw,16px)",
            gridTemplateColumns: "repeat(11,1fr)",
            gridArea: "1/1/2/12",
            [theme.breakpoints.down("md")]: {
              display: "flex",
              flexDirection: "column",
              gap: "40px",
              paddingInline: "56px",
            },
          }}
        >
          <Typography
            sx={{
              opacity: 1,
              visibility: "inherit",
              pointerEvents: "none",
              userSelect: "none",
              zIndex: 1,
              color: "#6985b41f",
              fontSize: "clamp(28px,3.125vw,60px)",
              fontWeight: "500",
              lineHeight: 1.1,
              position: "absolute",
              left: "clamp(50px,4.688vw,90px)",
              [theme.breakpoints.down("lg")]: { display: "none" },
            }}
          >
            03
          </Typography>
          <Typography
            data-aos="fade-up"
            sx={{
              gridArea: "1/2/2/3",
              fontWeight: "400",
              lineHeight: 1.6,
              fontSize: "clamp(14px,1.2vw,18px)",
              [theme.breakpoints.down("lg")]: {
                gridArea: "1/2/2/4",
              },
            }}
          >
            {t("home.chooseUs.subTitle")}
          </Typography>
          <Typography
            data-aos="fade-up"
            data-aos-duration="2000"
            sx={{
              gridArea: "1/6/2/10",
              fontSize: "clamp(28px,3.125vw,60px)",
              fontWeight: "500",
              [theme.breakpoints.down("lg")]: {
                gridArea: "1/6/2/11",
              },
            }}
          >
            {t("home.chooseUs.title")}
          </Typography>
          <Typography
            sx={{
              gridArea: "2/6/3/10",
              fontSize: "clamp(14px,.833vw,16px)",
              fontWeight: "400",
              lineHeight: 1.36,
              [theme.breakpoints.down("lg")]: {
                gridArea: "2/6/3/10",
              },
            }}
          >
            {t("home.chooseUs.content")}
          </Typography>
          <Box
            sx={{
              gridArea: "2/10/3/12",
              justifySelf: "end",
            }}
          >
            <AnimatedBlueButton text={t("home.chooseUs.button")} variant="outlined" />
          </Box>
        </Box>
        <Box
          ref={ref}
          className="content"
          sx={{
            display: "grid",
            mt: "clamp(40px,3.125vw,60px)",
            gap: "clamp(40px,6.25vw,120px) clamp(10px,.833vw,16px)",
            gridTemplateColumns: "repeat(11,1fr)",
            gridArea: "2/1/3/12",
            [theme.breakpoints.down("md")]: {
              display: "flex",
              gap: "40px",
              flexDirection: "column",
            },
          }}
        >
          <Box className="description" sx={{ display: "flex", gap: "32px", gridArea: "1/6/2/12", flexDirection: "column" }}>
            <Box
              sx={{
                clipPath: clipPath,
                transition: "clip-path 0.3s ease-out",
                aspectRatio: "872/500",
                width: "100%",
                display: "flex",
                position: "relative",
                [theme.breakpoints.down("md")]: {
                  aspectRatio: "280/340",
                },
              }}
            >
              <Box
                sx={{
                  zIndex: 2,
                  position: "absolute",
                  top: "clamp(32px,2.604vw,50px)",
                  right: "clamp(24px,3.083vw,70px)",
                  [theme.breakpoints.down("md")]: {
                    top: "60px",
                    left: "56px",
                    right: "56px",
                  },
                }}
              >
                {awardList.map((item, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      textAlign: "right",
                      flexDirection: "column",
                      alignItems: "flex-end",
                      transition: "all .3s ease-in-out .3s",
                      display: "flex",
                      position: "absolute",
                      top: 0,
                      right: 0,
                      transform: activeIndex === idx ? "none" : "translateY(20px)",
                      opacity: activeIndex === idx ? 1 : 0,
                      animationDelay: 0,
                      [theme.breakpoints.down("md")]: {
                        textAlign: "left",
                        alignItems: "flex-start",
                        width: "100%",
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: "clamp(250px,15.458vw,300px)",
                        fontSize: "clamp(22px,1.667vw,32px)",
                        lineHeight: 1.1,
                        fontWeight: "500",
                        color: "#323e59",
                        [theme.breakpoints.up("md")]: { wordBreak: "break-all", whiteSpace: "pre-wrap" },
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      sx={{
                        minWidth: "clamp(250px,15.458vw,300px)",
                        mt: "6px",
                        fontWeight: "400",
                        lineHeight: 1.6,
                        fontSize: "clamp(14px,.833vw,16px)",
                        color: "#323e59",
                      }}
                    >
                      {item.text}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  img: {
                    height: "120%",
                    objectFit: "cover",
                    width: "100%",
                  },
                }}
              >
                <img src={require("src/assets/images/home/chhandak-pradhan-oriLSL62wz8-unsplash.jpg")} alt="" />
              </Box>
            </Box>
            <Typography
              data-aos={smallMd ? "fade-up" : ""}
              sx={{
                fontWeight: "400",
                fontSize: "clamp(14px,.833vw,16px)",
                lineHeight: 1.6,
                [theme.breakpoints.down("md")]: { paddingInline: "56px" },
              }}
            >
              {t("home.chooseUs.description")}
            </Typography>
          </Box>
          <Box
            data-aos="fade-up"
            sx={{
              display: "flex",
              gap: "clamp(12px,.729vw,14px)",
              gridArea: "1/2/2/5",
              flexDirection: "column",
              width: "100%",
              position: "relative",
              ".MuiTypography-root": {
                fontSize: "clamp(14px,.833vw,16px)",
                fontWeight: "500",
                lineHeight: 1.4,
                letterSpacing: ".04em",
                [theme.breakpoints.down("md")]: { paddingInline: "56px" },
              },
            }}
          >
            <Box
              sx={{
                width: "100%",
                display: "flex",
                overflow: "hidden",
                clipPath: clipPath,
                transition: "clip-path 0.3s ease-out",
                aspectRatio: "184/108",
                mb: "30px",
                img: {
                  height: "100%",
                  objectFit: "cover",
                  width: "100%",
                },
              }}
            >
              <img src={require("src/assets/images/home/javier-allegue-barros-C7B-ExXpOIE-unsplash.jpg")} alt="" />
            </Box>
            <Typography> {t("home.chooseUs.slogan1")}</Typography>
            <Typography> {t("home.chooseUs.slogan2")}</Typography>
            <Typography> {t("home.chooseUs.slogan3")}</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ReasonsToChooseUs;
